import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { AxiosError } from 'axios';
import toast from 'react-hot-toast';

import { RegisterParceiroHandle } from '../../../components/RegisterParceiro/RegisterParceiro';
import { IParceiro } from '../../../interfaces/response/Parceiros/IParceiro';

import {
  getParceirosColeta,
  getParceirosEntrega,
} from '../../../redux/features/parceiros/parceirosThunk';
import { useAsyncDispatch } from '../../../redux/store';

export type TipoParceiro = 'entrega' | 'coleta';

export interface UseParceirosResult {
  parceiros: IParceiro[];
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  filter: string;
  loading: boolean;
  handleGetParceiros: () => void;
  modalRef: RefObject<RegisterParceiroHandle>;
  activeTab: TipoParceiro;
  setActiveTab: React.Dispatch<React.SetStateAction<TipoParceiro>>;
}

const normalize = (v: string): string => (v ?? '').toLowerCase().trim();
const normalizeCep = (cep: string): string => (cep ?? '').replace(/\D/g, '');

const useParceiros = (): UseParceirosResult => {
  const [filter, setFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TipoParceiro>('entrega');
  const modalRef = useRef<RegisterParceiroHandle>(null);
  const dispatch = useAsyncDispatch();

  const [parceiros, setParceiros] = useState<IParceiro[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const filteredParceiros = useMemo(() => {
    if (!filter) return parceiros;

    const f = normalize(filter);
    const fCep = normalizeCep(filter);

    return parceiros.filter((p) => {
      const sigla = normalize(p.sigla);
      const ci = normalizeCep(p.cepInicio);
      const cf = normalizeCep(p.cepFim);

      // procura por sigla OU por CEP (com ou sem máscara)
      return (
        sigla.includes(f) ||
        ci.includes(fCep) ||
        cf.includes(fCep) ||
        ci.includes(f) ||
        cf.includes(f)
      );
    });
  }, [filter, parceiros]);

  const consultarParceiros = async (tipo: TipoParceiro): Promise<void> => {
    setLoading(true);

    const thunk = tipo === 'entrega' ? getParceirosEntrega : getParceirosColeta;

    await dispatch(thunk())
      .unwrap()
      .then((response: IParceiro[]) => {
        // conforme o contrato descrito: getAll retorna array
        setParceiros(response);
      })
      .catch((error) => {
        const axiosError = error as AxiosError;
        if (axiosError.code === 'ERR_CANCELED') return;
        toast.error('Erro ao consultar parceiros, por favor tente mais tarde');
      })
      .finally(() => setLoading(false));
  };

  const handleGetParceiros = (): void => {
    consultarParceiros(activeTab);
  };

  useEffect(() => {
    consultarParceiros(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return {
    parceiros: filteredParceiros,
    setFilter,
    filter,
    loading,
    handleGetParceiros,
    modalRef,
    activeTab,
    setActiveTab,
  };
};

export default useParceiros;
